/**
 * One department's card on Department Status.
 *
 * Full-width and stacked, matching the legacy build: a header carrying the name, the type
 * badge and the department's own headline chip; three big centred metrics; a load bar
 * against that department's own benchmark; a verdict callout; and the Manager Insight card
 * — the judgement layer a formula cannot provide, and the only signal a judgement
 * department has at all.
 *
 * Each signal type reports in its own units. A coverage problem is not a fractional seat,
 * and a support ratio is not an FTE gap, so nothing here is flattened onto a shared score.
 */
import { useState } from 'react';
import { useModel } from '../state.jsx';

/** Presentational status, in the order the gates are applied. */
export function statusOf(signal) {
  if (signal.dataInvalid) return { key: 'dataInvalid', label: 'Data Invalid', color: 'var(--purple)' };
  if (signal.notConfigured) return { key: 'notConfigured', label: 'Not Configured', color: 'var(--grey)' };
  if (signal.flagged) return { key: 'flagged', label: 'Flagged', color: 'var(--red)' };
  if (signal.watch) return { key: 'watch', label: 'Watch', color: 'var(--amber)' };
  if (signal.kind === 'judgment') return { key: 'informational', label: 'Informational', color: 'var(--blue)' };
  return { key: 'healthy', label: 'Healthy', color: 'var(--grn)' };
}

const TYPE_BADGE = {
  judgment: { label: 'JUDGMENT', color: 'var(--amber)', bg: 'var(--ltamber)', title: 'Judgment department: no formula by design' },
  support: { label: 'SUPPORT', color: '#6366f1', bg: '#eef2ff', title: 'Support function: staffing governed by lane thresholds' },
  service: { label: 'SERVICE', color: 'var(--teal)', bg: 'var(--teal3)', title: 'Service department: volume-driven capacity model' },
};

function TypeBadge({ type }) {
  const t = TYPE_BADGE[type] ?? TYPE_BADGE.service;
  return (
    <span
      title={t.title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 700,
        color: t.color, background: t.bg, border: `1px solid ${t.color}`,
        borderRadius: 4, padding: '1px 5px', letterSpacing: '.5px',
      }}
    >
      {t.label}
    </span>
  );
}

function Metric({ value, label, color }) {
  return (
    <div className="dept-metric">
      <div className="dept-metric-val" style={color ? { color } : undefined}>{value}</div>
      <div className="dept-metric-lbl" style={{ lineHeight: 1.2 }} dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}

function Bar({ pct, color }) {
  return (
    <div className="util-bar-wrap">
      <div className="util-bar" style={{ width: `${Math.max(0, Math.min(pct, 110))}%`, background: color }} />
    </div>
  );
}

/** The three headline metrics, the load bar, and the right-hand chip, per signal type. */
function useTileShape(dep, signal, state) {
  const filled = state.team.filter((e) => e.dept === dep.id && !e.vacant && !e._builtStale).length;
  const vacancies = state.team.filter((e) => e.dept === dep.id && e.vacant && !e._builtStale);

  if (signal.notConfigured) {
    return {
      chip: <span className="chip chip-grey">Not configured</span>,
      metrics: [
        <Metric key="a" value="—" label="No Roles<br>Loaded" color="var(--ink4)" />,
        <Metric key="b" value="—" label="No Roster<br>Loaded" color="var(--ink4)" />,
        <Metric key="c" value="—" label="No<br>Signal" color="var(--ink4)" />,
      ],
      filled, vacancies,
    };
  }

  if (signal.dataInvalid) {
    return {
      chip: <span className="chip" style={{ background: '#F5F3FF', color: '#5B21B6', border: '1px solid #5B21B6' }}>Cannot assess</span>,
      metrics: [
        <Metric key="a" value="—" label="Inputs<br>Invalid" color="var(--purple)" />,
        <Metric key="b" value={filled} label="Staff" />,
        <Metric key="c" value={signal.invalidReasons.length} label="Broken<br>Inputs" color="var(--purple)" />,
      ],
      filled, vacancies,
    };
  }

  if (signal.kind === 'volume') {
    const gapColor = signal.flagged ? 'var(--red)' : signal.watch ? 'var(--amber)' : 'var(--grn)';
    const trig = dep.fteTrig ?? 0.5;
    return {
      chip: (
        <span className="chip" style={{
          background: signal.flagged ? 'var(--ltred)' : signal.watch ? 'var(--ltamber)' : 'var(--ltgrn)',
          color: gapColor, border: `1px solid ${gapColor}`,
        }}>
          {signal.volGap > 0 ? `${signal.volGap} FTE short` : 'Within capacity'}
        </span>
      ),
      metrics: [
        <Metric key="a" value={signal.impliedFTE} label="FTE Implied" color={gapColor} />,
        <Metric key="b" value={filled} label="Staff" color={vacancies.length ? 'var(--amber)' : undefined} />,
        <Metric key="c" value={signal.volGap} label="Seat Gap" color={gapColor} />,
      ],
      // The bar reads the gap against this department's own action threshold, which is
      // where Watch becomes Flagged — not against some org-wide line.
      bar: { pct: trig ? (signal.volGap / trig) * 100 : 0, color: gapColor },
      barLabel: (
        <>
          <span>Productive {signal.productiveFTE} of {signal.impliedFTE} FTE implied</span>
          <span style={{ marginLeft: 'auto', color: gapColor, fontWeight: 700 }}>
            gap {signal.volGap} vs {trig} threshold
          </span>
        </>
      ),
      filled, vacancies,
    };
  }

  if (signal.kind === 'coverage') {
    const covColor = signal.covUncovered > 0 ? 'var(--red)' : signal.covSingle > 0 ? 'var(--amber)' : 'var(--grn)';
    const blocks = 6;
    return {
      chip: (
        <span className="chip" style={{
          background: signal.covUncovered ? 'var(--ltred)' : signal.covSingle ? 'var(--ltamber)' : 'var(--ltgrn)',
          color: covColor, border: `1px solid ${covColor}`,
        }}>
          {signal.covUncovered} uncovered
        </span>
      ),
      metrics: [
        <Metric key="a" value={signal.covUncovered} label="Uncovered" color={covColor} />,
        <Metric key="b" value={filled} label="Staff" color={vacancies.length ? 'var(--amber)' : undefined} />,
        <Metric key="c" value={signal.covSingle} label="Single-Point" color={signal.covSingle ? 'var(--amber)' : 'var(--grn)'} />,
      ],
      bar: { pct: ((signal.covUncovered + signal.covSingle) / blocks) * 100, color: covColor },
      barLabel: (
        <>
          <span>{blocks - signal.covUncovered - signal.covSingle} of {blocks} time blocks resilient</span>
          <span style={{ marginLeft: 'auto', color: covColor, fontWeight: 700 }}>
            {signal.covUncovered} uncovered · {signal.covSingle} single-point
          </span>
        </>
      ),
      filled, vacancies,
    };
  }

  if (signal.kind === 'support') {
    const benchmark = signal.benchmark;
    const ratio = signal.hcRatioActual;
    const ratioColor = ratio <= benchmark ? 'var(--grn)' : ratio <= Math.round(benchmark * 1.25) ? 'var(--amber)' : 'var(--red)';
    const hasVolLane = signal.volLoadPct > 0;
    const hasReqLane = signal.reqUtilPct > 0;
    const openReqs = state.team.filter((e) => e.dept === dep.id && e.vacant).length;

    return {
      chip: (
        <span className="chip" style={{
          background: ratio <= benchmark ? 'var(--ltgrn)' : ratio <= Math.round(benchmark * 1.25) ? 'var(--ltamber)' : 'var(--ltred)',
          color: ratioColor, border: `1px solid ${ratioColor}`,
        }}>
          {ratio}:1 wtd
        </span>
      ),
      metrics: [
        <Metric key="a" value={`${ratio}:1`} label="Weighted<br>HC Ratio" color={ratioColor} />,
        hasVolLane
          ? <Metric key="b" value={`${signal.volLoadPct}%`} label="Payroll<br>Volume" color={signal.volAlert ? 'var(--red)' : signal.volLoadPct > 80 ? 'var(--amber)' : 'var(--grn)'} />
          : <Metric key="b" value={`${ratio}:1`} label="Flat<br>Ratio" color={ratioColor} />,
        hasReqLane
          ? <Metric key="c" value={openReqs} label="Open<br>Reqs" color={signal.reqAlert ? 'var(--red)' : undefined} />
          : <Metric key="c" value={state.settings.orgHeadcount} label="Org<br>Headcount" color="var(--ink3)" />,
      ],
      // Ratio against 1.25x benchmark, which is that lane's own alert line.
      bar: { pct: (ratio / (benchmark * 1.25)) * 100, color: ratioColor },
      barLabel: (
        <>
          <span>Headcount-Exposed Ratio</span>
          <span style={{ marginLeft: 'auto', color: ratioColor, fontWeight: 700 }}>
            {ratio}:1 · {signal.hcStaff} FTE vs 1:{benchmark} benchmark
          </span>
        </>
      ),
      filled, vacancies,
    };
  }

  // Judgment — no formula, ever.
  return {
    chip: <span className="chip chip-blue">Judgment</span>,
    metrics: [
      <Metric key="a" value="—" label="No<br>Formula" color="var(--ink4)" />,
      <Metric key="b" value={filled} label="Staff" />,
      <Metric key="c" value={signal.flagged ? '🚨' : signal.watch ? '⚠️' : '—'} label="Manager<br>Read" />,
    ],
    filled, vacancies,
  };
}

const COMPLETENESS = [['', 'Not yet set'], ['behind', '🔴 Falling behind'], ['pace', '🟡 Keeping pace'], ['ahead', '🟢 Ahead / clearing']];
const QUALITY = [['', 'Not yet set'], ['below', '🔴 Below standard'], ['meets', '🟡 Meets standard'], ['exceeds', '🟢 Exceeds standard']];
const MASTERY = [['', 'Not yet set'], ['A', 'A — expert'], ['B', 'B — proficient'], ['C', 'C — developing'], ['D', 'D — struggling'], ['F', 'F — needs intervention']];

/**
 * Manager Insight. Three dropdowns and a note — no score, no weighting. The escalation
 * rule is deliberately coarse: it can raise a department's status, never lower it, and it
 * does not pretend to be a precise measurement.
 */
function ManagerInsight({ dep }) {
  const { state, setMapValue, isAdmin } = useModel();
  const signal = state.managerSignals?.[dep.id] ?? {};
  const [note, setNote] = useState(signal.note ?? '');
  const [error, setError] = useState(null);

  const negative = (field, value) =>
    (field === 'completeness' && value === 'behind') ||
    (field === 'quality' && value === 'below') ||
    (field === 'mastery' && (value === 'D' || value === 'F'));

  async function set(field, value) {
    setError(null);
    try {
      await setMapValue('managerSignals', dep.id, { ...signal, [field]: value }, { ...signal, [field]: value });
    } catch (err) { setError(err.message); }
  }

  const colorFor = (field, value) =>
    !value ? 'var(--ink4)' : negative(field, value) ? 'var(--red)' : 'var(--grn)';

  const Field = ({ label, field, options }) => (
    <div>
      <div style={{ fontSize: 9, color: 'var(--ink4)', marginBottom: 2 }}>{label}</div>
      <select
        className="ei"
        disabled={!isAdmin}
        value={signal[field] ?? ''}
        onChange={(e) => set(field, e.target.value)}
        style={{
          width: '100%', fontSize: 10, fontWeight: 600,
          color: colorFor(field, signal[field]),
          borderBottomColor: colorFor(field, signal[field]),
        }}
      >
        {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
      </select>
    </div>
  );

  const anySet = signal.completeness || signal.quality || signal.mastery;

  return (
    <div style={{
      marginTop: 10, padding: '10px 12px', background: 'var(--bg)',
      border: '1px solid var(--border)', borderRadius: 'var(--rsm)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink2)' }}>Manager Insight</span>
        <span style={{ marginLeft: 'auto', fontSize: 9, color: 'var(--ink4)' }}>
          {anySet ? `Updated ${(signal.updatedAt ?? '').slice(0, 10)}` : 'Not yet set'}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
        <Field label="Completeness" field="completeness" options={COMPLETENESS} />
        <Field label="Quality" field="quality" options={QUALITY} />
        <Field label="Team mastery" field="mastery" options={MASTERY} />
      </div>
      <textarea
        className="ei"
        disabled={!isAdmin}
        placeholder="Optional note — what's driving this? (e.g. new client onboarding, two people out, backlog clearing)"
        style={{ width: '100%', fontSize: 10, minHeight: 32, resize: 'vertical', marginTop: 8 }}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => note !== (signal.note ?? '') && set('note', note)}
      />
      {error && <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 4 }}>{error}</div>}
    </div>
  );
}

export function DeptCard({ dep, signal, severity, onFilter }) {
  const { state, engine } = useModel();
  const [open, setOpen] = useState(false);
  const status = statusOf(signal);
  const shape = useTileShape(dep, signal, state);

  const children = state.depts.filter((d) => d.parent === dep.id);
  const projects = state.impls.filter((p) => p.dept === dep.id && p.wksLeft > 0);

  const verdictClass = signal.flagged ? 'act' : signal.watch ? 'watch' : 'ok';
  const verdictIcon = signal.dataInvalid ? '🛑'
    : signal.notConfigured ? '⚙️'
      : signal.flagged ? '🚨' : signal.watch ? '⚠️' : signal.kind === 'judgment' ? '🎯' : '✅';

  return (
    <div className="dept-card" style={{ borderTop: `3px solid ${status.color}`, marginBottom: 14 }}>
      <div className="dept-card-hdr">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span
            className="dept-card-name"
            style={{ color: dep.color, cursor: 'pointer' }}
            onClick={() => onFilter?.(dep.id)}
          >
            {dep.name}
          </span>
          <TypeBadge type={dep.type ?? 'service'} />
          {dep.approvedForReporting && (
            <span className="chip chip-green" style={{ fontSize: 9 }}>Validated</span>
          )}
        </div>
        {shape.chip}
      </div>

      <div className="dept-card-body">
        <div className="dept-card-metrics">{shape.metrics}</div>

        {shape.bar && (
          <>
            <div style={{ display: 'flex', fontSize: 10, color: 'var(--ink4)', marginBottom: 3 }}>
              {shape.barLabel}
            </div>
            <Bar pct={shape.bar.pct} color={shape.bar.color} />
          </>
        )}

        <div className={`verdict ${verdictClass}`} style={{ marginTop: 10 }}>
          <span className="verdict-icon">{verdictIcon}</span>
          <div>
            <div className="verdict-title" style={{ color: status.color }}>
              {signal.dataInvalid ? 'Data Invalid — Cannot Assess'
                : signal.notConfigured ? 'Not Configured'
                  : signal.flagged ? 'Action Required'
                    : signal.watch ? 'Monitoring'
                      : signal.kind === 'judgment' ? 'Informational — Manager Judgment'
                        : 'Within Threshold'}
            </div>
            <div className="verdict-sub">
              {signal.dataInvalid ? signal.invalidReasons.join('; ')
                : signal.notConfigured ? 'No roles or roster loaded — a data gap, not a proven staffing shortage. Excluded from every KPI total.'
                  : signal.kind === 'volume' ? `Implied ${signal.impliedFTE} FTE against ${signal.productiveFTE} productive · threshold ${dep.fteTrig ?? 0.5}`
                    : signal.kind === 'coverage' ? (signal.allSeatsVacant ? 'Every seat on this desk is vacant — Flagged regardless of the grid.' : 'Coverage is measured by time block, not by an hours average.')
                      : signal.kind === 'support' ? (signal.lanesOverThreshold?.length ? `Over threshold: ${signal.lanesOverThreshold.join(', ')}` : 'All lanes within benchmark')
                        : 'No formula applies by design — the manager’s read is the only signal.'}
            </div>
          </div>
        </div>

        {shape.vacancies.length > 0 && (
          <div style={{
            fontSize: 11, color: 'var(--amber)', fontWeight: 600, marginTop: 5,
            padding: '6px 8px', background: '#fffbeb', borderRadius: 5, border: '1px solid var(--amber)',
          }}>
            {shape.vacancies.length} vacant seat{shape.vacancies.length > 1 ? 's' : ''} —
            capacity removed from the model. Figures reflect the remaining {shape.filled} filled seat
            {shape.filled === 1 ? '' : 's'}.
          </div>
        )}

        {projects.length > 0 && (
          <div style={{ fontSize: 10, color: 'var(--ink4)', marginTop: 5, paddingTop: 5, borderTop: '1px dashed var(--border)' }}>
            🔧 {projects.length} active project{projects.length !== 1 ? 's' : ''} (
            {projects.reduce((s, p) => s + (p.hrs ?? 0), 0)} hrs/wk)
            {signal.kind === 'volume'
              ? ' — included in implied FTE above.'
              : ' — not reflected in this department’s status; there is no honest way to fold temporary project hours into this signal type.'}
          </div>
        )}

        <ManagerInsight dep={dep} />

        {children.length > 0 && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.6px', color: 'var(--ink4)', textTransform: 'uppercase', marginBottom: 6 }}>
              Division Rollup — Macro View
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {children.map((child) => {
                const s = engine.computeDeptSignal(child);
                const c = statusOf(s);
                return (
                  <span
                    key={child.id}
                    className="chip"
                    style={{ background: 'var(--grey2)', color: 'var(--ink2)', cursor: 'pointer' }}
                    onClick={() => onFilter?.(child.id)}
                  >
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.color, display: 'inline-block' }} />
                    {child.name}
                    <span style={{ color: 'var(--ink4)' }}>
                      {s.notConfigured ? 'no roster' : s.kind === 'volume' ? `${s.volGap} gap` : s.kind === 'support' ? `${s.hcRatioActual}:1` : c.label.toLowerCase()}
                    </span>
                  </span>
                );
              })}
            </div>
            <div style={{ fontSize: 10, color: 'var(--ink4)', marginTop: 5 }}>
              Metrics above reflect {dep.name}’s own roles only. Click a chip to drill into that department.
            </div>
          </div>
        )}
      </div>

      <button className="expand-btn" onClick={() => setOpen((o) => !o)}>
        {open ? '▲ Hide detail' : '▼ Show detail & actions'}
      </button>

      <div className={`dept-card-detail${open ? ' open' : ''}`}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
          <div>
            <div className="config-section-label">Signal</div>
            <div style={{ fontSize: 11, color: 'var(--ink2)', lineHeight: 1.7 }}>
              Type: <strong>{signal.kind}</strong><br />
              Status: <strong style={{ color: status.color }}>{status.label}</strong><br />
              {severity != null && <>Severity: <strong className="num-mono">{Math.round(severity)}</strong> / 100<br /></>}
              Validated for reporting: <strong>{dep.approvedForReporting ? 'Yes' : 'Not yet'}</strong>
            </div>
          </div>

          {signal.kind === 'volume' && (
            <div>
              <div className="config-section-label">Volume drivers</div>
              <div style={{ fontSize: 11, color: 'var(--ink2)', lineHeight: 1.7 }}>
                {dep.volDrivers.map((d, i) => (
                  <div key={i}>
                    {d.name}: <span className="num-mono">{d.current?.toLocaleString()}</span> ÷{' '}
                    <span className="num-mono">{d.perFTE}</span> = <strong className="num-mono">{signal.driverNeeds[i]}</strong> FTE
                  </div>
                ))}
                {signal.multiDriver && (
                  <div style={{ marginTop: 4, color: 'var(--ink4)' }}>
                    Combined with <strong>{signal.combineMode.toUpperCase()}</strong>
                    {signal.combineMode === 'max'
                      ? ' — drivers treated as alternative measures of the same work.'
                      : ' — drivers treated as separate, additive work.'}
                    {!signal.volCombineLocked && ' Methodology not yet locked.'}
                  </div>
                )}
              </div>
            </div>
          )}

          {signal.kind === 'support' && (
            <div>
              <div className="config-section-label">Lanes</div>
              <div style={{ fontSize: 11, color: 'var(--ink2)', lineHeight: 1.7 }}>
                Headcount: <strong className="num-mono">{signal.hcRatioActual}:1</strong> vs 1:{signal.benchmark}<br />
                {signal.reqUtilPct > 0 && <>Requisition load: <strong className="num-mono">{signal.reqUtilPct}%</strong><br /></>}
                {signal.volLoadPct > 0 && <>Volume ceiling: <strong className="num-mono">{signal.volLoadPct}%</strong><br /></>}
                <span style={{ color: 'var(--ink4)' }}>Each lane is thresholded independently rather than blended into one ratio.</span>
              </div>
            </div>
          )}

          <div>
            <div className="config-section-label">Roster</div>
            <div style={{ fontSize: 11, color: 'var(--ink2)', lineHeight: 1.7 }}>
              Filled: <strong className="num-mono">{shape.filled}</strong><br />
              Vacant: <strong className="num-mono">{shape.vacancies.length}</strong><br />
              Recruitable: <strong className="num-mono">
                {shape.vacancies.filter((e) => engine.isVacancyRecruitable(e)).length}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
